/*
 * Copyright (c) 2023 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import {Injectable} from '@angular/core';
import {
  EmailablePayloadWithContactsMapDTO,
  ExternalInvoiceOrderListVoModel,
  InvoiceCancelSelectVo,
  InvoiceDTO,
  InvoiceExternalDtoModel,
  InvoiceExternalMetaModel,
  InvoiceListMeta,
  InvoiceListVO,
  InvoiceMeta,
  InvoiceReportGenerationVo
} from '@well/domain';
import { HttpClient } from '@angular/common/http';
import {EnvironmentService} from "@irian-ro/util-environment";
import {Observable} from 'rxjs';
import {LazyLoadEvent} from "primeng/api";
import { AbstractResourceService, AdditionalParams, JsonApiListResourceMetadataBase, JsonApiResource, QueryParams } from '@irian-ro/abstract-resource';

@Injectable({
  providedIn: 'root'
})
export class CustomerInvoiceResourceService extends AbstractResourceService<InvoiceListVO, InvoiceDTO, InvoiceListMeta, InvoiceMeta> {

  constructor(httpClient: HttpClient, environmentService: EnvironmentService) {
    super(
      httpClient,
      environmentService.getEnvironmentProperty('customerInvoiceUrl'),
      InvoiceListVO,
      InvoiceDTO
    );
  }

  saveExternalReceivedInvoice(externalInvoiceWithContacts: EmailablePayloadWithContactsMapDTO<InvoiceExternalDtoModel>): Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> {
    return <Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>>>this.httpClient?.post(`${this.url}/external`, externalInvoiceWithContacts);
  }

  getExternalReceivedInvoiceTemplate(): Observable<JsonApiResource<InvoiceExternalDtoModel, InvoiceExternalMetaModel>> {
    return <Observable<JsonApiResource<InvoiceExternalDtoModel, InvoiceExternalMetaModel>>>this.httpClient?.get(`${this.url}/external/template`);
  }

  getExternalInvoiceableOrderSentToVendor(vendorUuid: string, currencyCode: string, includeOnlyNotGeneratedInvoices: boolean): Observable<JsonApiResource<ExternalInvoiceOrderListVoModel[], JsonApiListResourceMetadataBase>> {
    return <Observable<JsonApiResource<ExternalInvoiceOrderListVoModel[], JsonApiListResourceMetadataBase>>>this.httpClient?.get(`${this.url}/external/orders/${vendorUuid}`, {
      params: {
        currencyCode: currencyCode,
        includeOnlyNotGeneratedInvoices: includeOnlyNotGeneratedInvoices
      }
    });
  }

  getExternalInvoiceableOrderSentToVendorForInvoice(vendorUuid: string, invoiceUuid: string, currencyCode: string, includeOnlyNotGeneratedInvoices: boolean): Observable<JsonApiResource<ExternalInvoiceOrderListVoModel[], JsonApiListResourceMetadataBase>>{
    return <Observable<JsonApiResource<ExternalInvoiceOrderListVoModel[], JsonApiListResourceMetadataBase>>>this.httpClient?.get(`${this.url}/external/orders/${vendorUuid}/invoice/${invoiceUuid}`, {
      params: {
        currencyCode: currencyCode,
        includeOnlyNotGeneratedInvoices: includeOnlyNotGeneratedInvoices
      }
    });
  }

  getExternalReceivedInvoiceDetails(uuid: string): Observable<JsonApiResource<InvoiceExternalDtoModel, InvoiceExternalMetaModel>> {
    return <Observable<JsonApiResource<InvoiceExternalDtoModel, InvoiceExternalMetaModel>>>this.httpClient?.get(`${this.url}/external/details/${uuid}`);
  }

  updateExternalReceivedInvoice(externalInvoiceWithContacts: EmailablePayloadWithContactsMapDTO<InvoiceExternalDtoModel>): Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> {
    return <Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> >this.httpClient?.put(`${this.url}/external`, externalInvoiceWithContacts);
  }

  getCancelableReceivedInvoices(vendorUuid: string, event: LazyLoadEvent, additionalParams?: Array<AdditionalParams>): Observable<JsonApiResource<InvoiceCancelSelectVo[], JsonApiListResourceMetadataBase>>{
    const queryString = new QueryParams(event, additionalParams ?? []);

    return <Observable<JsonApiResource<InvoiceCancelSelectVo[], JsonApiListResourceMetadataBase>>>this.httpClient?.get(`${this.url}/cancelable/${vendorUuid}${queryString}`);
  }

  getInvoicesReport(invoiceUuids: string[], currencyCode: string, fromBankAccountUuid: string): Observable<JsonApiResource<InvoiceReportGenerationVo[], JsonApiListResourceMetadataBase>>{
    return <Observable<JsonApiResource<InvoiceReportGenerationVo[], JsonApiListResourceMetadataBase>>>this.httpClient?.get(`${this.url}/invoice-report`, {
      params: {invoiceUuids: invoiceUuids.join(","),
               currencyCode: currencyCode,
               fromBankAccountUuid: fromBankAccountUuid
      }
    });
  }

}
